import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DatasetService } from "../../../services/DatasetService";
import { usePermissions } from "../../../PermissionsProvider";
import { FinetuneDataset } from "../../../model_gen/finetuning";
import { ChatCompletionRequest } from "../../../model_gen/chat";
import moment from "moment";
import RequestViewModal from "../../Request/RequestViewModal";
import DatasetEligibleRequestsTable from "./DatasetEligibleRequestsTable";
import DatasetAddedRequestsTable from "./DatasetAddedRequestsTable";
import AIReview from "./AIReview";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Switch } from "../../ui/switch";
import { 
  PlusIcon,
  FileTextIcon,
  CalendarIcon,
  ServerIcon,
  SearchIcon,
  XIcon,
  Loader2Icon,
  FlaskConicalIcon
} from "lucide-react";
import { cn } from "../../../lib/utils";

// Add interface for tag distribution
interface TagDistribution {
  tag: string;
  count: number;
}

interface AIReviewPayload {
  correction_type: "freeform" | "choices";
  choices?: string[];
}

const DatasetEdit: React.FC = () => {
  const { id } = useParams();
  const { apiKey } = usePermissions();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [dataset, setDataset] = useState<FinetuneDataset | null>(null);

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  // Added requests state
  const [currentAddedPage, setCurrentAddedPage] = useState<number>(1);
  const [addedPageSize] = useState<number>(10);
  const [addedRequests, setAddedRequests] = useState<ChatCompletionRequest[]>([]);
  const [totalAdded, setTotalAdded] = useState<number>(0);
  const [isAddedLoading, setIsAddedLoading] = useState<boolean>(false);
  const addedRequestsCacheRef = useRef<{ [page: number]: ChatCompletionRequest[]; }>({});
  const [selectedAddedRequests, setSelectedAddedRequests] = useState<ChatCompletionRequest[]>([]);
  const [showOnlySelectedAdded, setShowOnlySelectedAdded] = useState(false);

  // Eligible requests state
  const [currentEligiblePage, setCurrentEligiblePage] = useState<number>(1);
  const [eligiblePageSize] = useState<number>(10);
  const [eligibleRequests, setEligibleRequests] = useState<ChatCompletionRequest[]>([]);
  const [totalEligible, setTotalEligible] = useState<number>(0);
  const [isEligibleLoading, setIsEligibleLoading] = useState<boolean>(false);
  const eligibleRequestsCacheRef = useRef<{ [page: number]: ChatCompletionRequest[]; }>({});
  const [selectedEligibleRequests, setSelectedEligibleRequests] = useState<ChatCompletionRequest[]>([]);
  const [showOnlySelectedEligible, setShowOnlySelectedEligible] = useState(false);

  const [tagDistribution, setTagDistribution] = useState<TagDistribution[]>([]);
  const [showAllTags, setShowAllTags] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("added");
  const [sessionIdFilter, setSessionIdFilter] = useState<string>("");
  const [appliedSessionIdFilter, setAppliedSessionIdFilter] = useState<string>("");
  const [isSearchMode, setIsSearchMode] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<ChatCompletionRequest[]>([]);
  const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false);
  const [eligibleRefreshTrigger, setEligibleRefreshTrigger] = useState<number>(0);
  const [addedRefreshTrigger, setAddedRefreshTrigger] = useState<number>(0);

  // Modal state
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ChatCompletionRequest | null>(null);

  // Added states for the Added Requests tab search
  const [addedSearchResults, setAddedSearchResults] = useState<ChatCompletionRequest[]>([]);
  const [isAddedSearchMode, setIsAddedSearchMode] = useState<boolean>(false);
  const [isAddedSearchLoading, setIsAddedSearchLoading] = useState<boolean>(false);

  // Request type filter state
  const [requestTypeFilters, setRequestTypeFilters] = useState(() => ({
    SYNTHETIC: true,
    PROD: true,
    RERUN: true,
  }));

  // Enhanced search state
  const [searchType, setSearchType] = useState<string>("query");
  const [searchValue, setSearchValue] = useState<string>("");
  const [responseKeywords, setResponseKeywords] = useState<string[]>([]);
  const [responseKeywordInput, setResponseKeywordInput] = useState<string>("");

  // Added search type states for Added Requests tab
  const [addedSearchType, setAddedSearchType] = useState<string>("query");
  const [addedSearchValue, setAddedSearchValue] = useState<string>("");
  const [addedResponseKeywords, setAddedResponseKeywords] = useState<string[]>([]);
  const [addedResponseKeywordInput, setAddedResponseKeywordInput] = useState<string>("");

  // Augmentation modal state
  const [isAugmentModalVisible, setIsAugmentModalVisible] = useState(false);
  const [augmentRequestCount, setAugmentRequestCount] = useState<number>(1);
  const [selectedAugmentRequest, setSelectedAugmentRequest] = useState<ChatCompletionRequest | null>(null);
  const [isAugmenting, setIsAugmenting] = useState(false);

  // AI Review modal state
  const [isAiReviewModalVisible, setIsAiReviewModalVisible] = useState(false);
  const [isAiReviewLoading, setIsAiReviewLoading] = useState(false);

  // Helper to get selected request types as an array
  const getSelectedRequestTypes = () => {
    return Object.entries(requestTypeFilters)
      .filter(([, checked]) => checked)
      .map(([type]) => type);
  };

  // Handle request updates
  const handleViewRequest = (request: ChatCompletionRequest) => {
    setSelectedRequest(request);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedRequest(null);
  };

  const handleRequestSave = (
    updatedRequest: ChatCompletionRequest,
    table: "eligible" | "added"
  ) => {
    // Update the local state first
    if (table === "eligible") {
      setEligibleRequests((prev) =>
        prev.map((request) =>
          request.request_id === updatedRequest.request_id
            ? updatedRequest
            : request
        )
      );

      if (eligibleRequestsCacheRef.current[currentEligiblePage]) {
        eligibleRequestsCacheRef.current[currentEligiblePage] =
          eligibleRequestsCacheRef.current[currentEligiblePage].map((request) =>
            request.request_id === updatedRequest.request_id
              ? updatedRequest
              : request
          );
      }

      // Also update in search results if in search mode
      if (isSearchMode) {
        setSearchResults((prev) =>
          prev.map((request) =>
            request.request_id === updatedRequest.request_id
              ? updatedRequest
              : request
          )
        );
      }
    } else {
      setAddedRequests((prev) =>
        prev.map((request) =>
          request.request_id === updatedRequest.request_id
            ? updatedRequest
            : request
        )
      );

      if (addedRequestsCacheRef.current[currentAddedPage]) {
        addedRequestsCacheRef.current[currentAddedPage] =
          addedRequestsCacheRef.current[currentAddedPage].map((request) =>
            request.request_id === updatedRequest.request_id
              ? updatedRequest
              : request
          );
      }

      // Also update in search results if in search mode
      if (isAddedSearchMode) {
        setAddedSearchResults((prev) =>
          prev.map((request) =>
            request.request_id === updatedRequest.request_id
              ? updatedRequest
              : request
          )
        );
      }
    }
  };

  // Fetch dataset details
  useEffect(() => {
    if (id) {
      setIsLoading(true);
      DatasetService.getDataset(Number(id), setIsLoading, apiKey).subscribe(
        (data: FinetuneDataset) => {
          if (data) {
            setDataset(data);
            setName(data.name || "");
            setDescription(data.description || "");
          }
        },
        (error: any) => {
          console.error("Error fetching dataset:", error);
        }
      );
    }
  }, [id, apiKey]);

  // Fetch dataset requests (Added tab)
  useEffect(() => {
    if (id) {
      // Skip fetching if in search mode
      if (isAddedSearchMode) return;

      fetchAddedRequests(currentAddedPage);
    }
  }, [
    currentAddedPage,
    id,
    addedPageSize,
    addedRefreshTrigger,
    isAddedSearchMode,
  ]);

  // Fetch eligible requests (Eligible tab)
  useEffect(() => {
    if (id && activeTab === "eligible" && !isSearchMode) {
      fetchEligibleRequests(currentEligiblePage);
    }
  }, [
    currentEligiblePage,
    id,
    eligiblePageSize,
    activeTab,
    eligibleRefreshTrigger,
    isSearchMode,
    appliedSessionIdFilter, // Add missing dependency
    requestTypeFilters, // Add missing dependency
  ]);

  // Fetch tag distribution
  useEffect(() => {
    if (id) {
      DatasetService.getDatasetRequestDistribution(id, null, apiKey).subscribe(
        (response: TagDistribution[]) => {
          setTagDistribution(response);
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  }, [id]);

  // Debounced refetch when request type filters change (only for added requests)
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      // Only fetch added requests in debounced effect to avoid conflicts
      fetchAddedRequests(currentAddedPage);
    }, 300);
  }, [requestTypeFilters, currentAddedPage, addedPageSize]);

  // Clear caches when request type filters change to force backend refetch
  useEffect(() => {
    eligibleRequestsCacheRef.current = {};
    addedRequestsCacheRef.current = {};
  }, [requestTypeFilters]);

  const fetchAddedRequests = (page: number) => {
    // If in search mode, don't fetch from the regular endpoint
    if (isAddedSearchMode) {
      return;
    }

    if (addedRequestsCacheRef.current[page]) {
      setAddedRequests(addedRequestsCacheRef.current[page]);
      return;
    }

    const offset = (page - 1) * addedPageSize;
    setIsAddedLoading(true);

    const types = getSelectedRequestTypes();
    DatasetService.getDatasetRequests(
      Number(id),
      offset,
      addedPageSize,
      setIsAddedLoading,
      apiKey,
      types
    ).subscribe(
      (response: { items: ChatCompletionRequest[]; total: number }) => {
        setAddedRequests(response.items);
        setTotalAdded(response.total);
        addedRequestsCacheRef.current = {
          ...addedRequestsCacheRef.current,
          [page]: response.items,
        };
        setIsAddedLoading(false);
      },
      (error: any) => {
        console.error(error);
        setIsAddedLoading(false);
      }
    );
  };

  const fetchEligibleRequests = (page: number) => {
    // If in search mode, don't fetch from the regular endpoint
    if (isSearchMode) {
      return;
    }

    if (eligibleRequestsCacheRef.current[page] && !appliedSessionIdFilter) {
      setEligibleRequests(eligibleRequestsCacheRef.current[page]);
      return;
    }

    const offset = (page - 1) * eligiblePageSize;
    setIsEligibleLoading(true);

    const types = getSelectedRequestTypes();
    
    // Add timeout safety mechanism
    const timeoutId = setTimeout(() => {
      console.warn("Eligible requests fetch timeout - resetting loading state");
      setIsEligibleLoading(false);
    }, 30000); // 30 second timeout

    DatasetService.getEligibleDatasetRequests(
      Number(id),
      offset,
      eligiblePageSize,
      null, // Don't pass setIsEligibleLoading to the service to avoid conflicts
      apiKey,
      appliedSessionIdFilter, // Use appliedSessionIdFilter instead of sessionIdFilter
      types
    ).subscribe(
      (response: { items: ChatCompletionRequest[]; total: number }) => {
        clearTimeout(timeoutId);
        setEligibleRequests(response.items);
        setTotalEligible(response.total);

        // Only cache if no session filter is applied
        if (!appliedSessionIdFilter) {
          eligibleRequestsCacheRef.current = {
            ...eligibleRequestsCacheRef.current,
            [page]: response.items,
          };
        }

        setIsEligibleLoading(false);
      },
      (error: any) => {
        clearTimeout(timeoutId);
        console.error("Error fetching eligible requests:", error);
        setIsEligibleLoading(false);
      }
    );
  };

  const handleSearch = () => {
    if (searchType === "query") {
      handleQuerySearch();
    } else if (searchType === "session") {
      handleSessionSearch();
    } else if (searchType === "response") {
      handleResponseKeywordSearch();
    }
  };

  const handleQuerySearch = () => {
    if (!searchValue.trim()) {
      clearSearch();
      return;
    }

    setSessionIdFilter("");
    setAppliedSessionIdFilter("");

    setIsSearchLoading(true);
    setIsSearchMode(true);

    // Add timeout safety mechanism
    const timeoutId = setTimeout(() => {
      console.warn("Search timeout - resetting loading state");
      setIsSearchLoading(false);
    }, 30000); // 30 second timeout

    DatasetService.searchEligibleDatasetRequests(
      Number(id),
      searchValue,
      [],
      null, // Don't pass setIsSearchLoading to avoid conflicts
      apiKey
    ).subscribe(
      (items: ChatCompletionRequest[]) => {
        clearTimeout(timeoutId);
        const updatedItems = items.map((request) => {
          const selectedVersion = selectedEligibleRequests.find(
            (selected) => selected.request_id === request.request_id
          );
          return selectedVersion || request;
        });

        setSearchResults(updatedItems);
        setCurrentEligiblePage(1);
        const endIndex = Math.min(eligiblePageSize, updatedItems.length);
        setEligibleRequests(updatedItems.slice(0, endIndex));
        setTotalEligible(updatedItems.length);
        setIsSearchLoading(false);
      },
      (error: any) => {
        clearTimeout(timeoutId);
        console.error("Error in query search:", error);
        setIsSearchLoading(false);
      }
    );
  };

  const handleSessionSearch = () => {
    if (!searchValue.trim()) {
      clearSearch();
      return;
    }

    setAppliedSessionIdFilter(searchValue);
    setCurrentEligiblePage(1);
    eligibleRequestsCacheRef.current = {};
    setEligibleRefreshTrigger((prev) => prev + 1);
  };

  const handleResponseKeywordSearch = () => {
    if (responseKeywords.length === 0) {
      clearSearch();
      return;
    }

    setIsSearchLoading(true);
    setIsSearchMode(true);
    
    // Add timeout safety mechanism
    const timeoutId = setTimeout(() => {
      console.warn("Response keyword search timeout - resetting loading state");
      setIsSearchLoading(false);
    }, 30000); // 30 second timeout
    
    DatasetService.searchEligibleDatasetRequests(
      Number(id),
      searchValue,
      responseKeywords,
      null, // Don't pass setIsSearchLoading to avoid conflicts
      apiKey
    ).subscribe(
      (items: ChatCompletionRequest[]) => {
        clearTimeout(timeoutId);
        const updatedItems = items.map((request) => {
          const selectedVersion = selectedEligibleRequests.find(
            (selected) => selected.request_id === request.request_id
          );
          return selectedVersion || request;
        });

        setSearchResults(updatedItems);
        setCurrentEligiblePage(1);
        const endIndex = Math.min(eligiblePageSize, updatedItems.length);
        setEligibleRequests(updatedItems.slice(0, endIndex));
        setTotalEligible(updatedItems.length);
        setIsSearchLoading(false);
      },
      (error: any) => {
        clearTimeout(timeoutId);
        console.error("Error in response keyword search:", error);
        setIsSearchLoading(false);
      }
    );
  };

  // Additional handlers for added requests search
  const handleAddedSearch = () => {
    if (addedSearchType === "query") {
      handleAddedQuerySearch();
    } else if (addedSearchType === "session") {
      handleAddedSessionSearch();
    } else if (addedSearchType === "response") {
      handleAddedResponseKeywordSearch();
    }
  };

  const handleAddedQuerySearch = () => {
    if (!addedSearchValue.trim()) {
      clearAddedSearch();
      return;
    }

    setIsAddedSearchLoading(true);
    setIsAddedSearchMode(true);

    DatasetService.searchAddedDatasetRequests(
      Number(id),
      addedSearchValue,
      [],
      setIsAddedSearchLoading,
      apiKey
    ).subscribe(
      (items: ChatCompletionRequest[]) => {
        const updatedItems = items.map((request) => {
          const selectedVersion = selectedAddedRequests.find(
            (selected) => selected.request_id === request.request_id
          );
          return selectedVersion || request;
        });

        setAddedSearchResults(updatedItems);
        setCurrentAddedPage(1);
        const endIndex = Math.min(addedPageSize, updatedItems.length);
        setAddedRequests(updatedItems.slice(0, endIndex));
        setTotalAdded(updatedItems.length);
        setIsAddedSearchLoading(false);
      },
      (error: any) => {
        console.error(error);
        setIsAddedSearchLoading(false);
      }
    );
  };

  const handleAddedSessionSearch = () => {
    if (!addedSearchValue.trim()) {
      clearAddedSearch();
      return;
    }
    handleAddedQuerySearch();
  };

  const handleAddedResponseKeywordSearch = () => {
    if (addedResponseKeywords.length === 0) {
      clearAddedSearch();
      return;
    }

    setIsAddedSearchLoading(true);
    setIsAddedSearchMode(true);
    
    DatasetService.searchAddedDatasetRequests(
      Number(id),
      addedSearchValue,
      addedResponseKeywords,
      setIsAddedSearchLoading,
      apiKey
    ).subscribe(
      (items: ChatCompletionRequest[]) => {
        const updatedItems = items.map((request) => {
          const selectedVersion = selectedAddedRequests.find(
            (selected) => selected.request_id === request.request_id
          );
          return selectedVersion || request;
        });

        setAddedSearchResults(updatedItems);
        setCurrentAddedPage(1);
        const endIndex = Math.min(addedPageSize, updatedItems.length);
        setAddedRequests(updatedItems.slice(0, endIndex));
        setTotalAdded(updatedItems.length);
        setIsAddedSearchLoading(false);
      },
      (error: any) => {
        console.error(error);
        setIsAddedSearchLoading(false);
      }
    );
  };

  const handleAddSelectedRequests = () => {
    setIsAddedLoading(true);
    setIsEligibleLoading(true);
    const payload = {
      dataset_id: Number(id),
      requests: selectedEligibleRequests.map((request) => ({
        request_id: request.request_id,
        metadata: request.metadata || {},
      })),
    };

    DatasetService.addDatasetRequests(
      Number(id),
      payload,
      null,
      apiKey
    ).subscribe(
      () => {
        addedRequestsCacheRef.current = {};
        eligibleRequestsCacheRef.current = {};

        setSelectedEligibleRequests([]);
        setCurrentAddedPage(1);
        setCurrentEligiblePage(1);
        setAddedRefreshTrigger((prev) => prev + 1);
        setEligibleRefreshTrigger((prev) => prev + 1);
        setIsAddedLoading(false);
        setIsEligibleLoading(false);
      },
      (error: any) => {
        console.error(error);
        setIsAddedLoading(false);
        setIsEligibleLoading(false);
      }
    );
  };

  const handleRemoveSelectedRequests = () => {
    setIsAddedLoading(true);
    setIsEligibleLoading(true);

    const payload = {
      dataset_id: Number(id),
      requests: selectedAddedRequests.map((request) => ({
        request_id: request.request_id,
        metadata: request.metadata || {},
      })),
    };

    DatasetService.removeDatasetRequests(
      Number(id),
      payload,
      null,
      apiKey
    ).subscribe(
      () => {
        addedRequestsCacheRef.current = {};
        eligibleRequestsCacheRef.current = {};

        setSelectedAddedRequests([]);
        setCurrentAddedPage(1);
        setCurrentEligiblePage(1);

        if (isAddedSearchMode) {
          setIsAddedSearchLoading(true);
          
          if (addedSearchType === "query" || addedSearchType === "session") {
            DatasetService.searchAddedDatasetRequests(
              Number(id),
              addedSearchValue,
              [],
              setIsAddedSearchLoading,
              apiKey
            ).subscribe(
              (items: ChatCompletionRequest[]) => {
                setAddedSearchResults(items);
                setAddedRequests(
                  items.slice(0, Math.min(addedPageSize, items.length))
                );
                setTotalAdded(items.length);
                setIsAddedSearchLoading(false);
                setIsAddedLoading(false);
              },
              (error: any) => {
                console.error(error);
                setIsAddedSearchLoading(false);
                setIsAddedLoading(false);
              }
            );
          } else if (addedSearchType === "response" && addedResponseKeywords.length > 0) {
            DatasetService.searchAddedDatasetRequests(
              Number(id),
              "",
              addedResponseKeywords,
              setIsAddedSearchLoading,
              apiKey
            ).subscribe(
              (items: ChatCompletionRequest[]) => {
                setAddedSearchResults(items);
                setAddedRequests(
                  items.slice(0, Math.min(addedPageSize, items.length))
                );
                setTotalAdded(items.length);
                setIsAddedSearchLoading(false);
                setIsAddedLoading(false);
              },
              (error: any) => {
                console.error(error);
                setIsAddedSearchLoading(false);
                setIsAddedLoading(false);
              }
            );
          } else {
            setAddedRefreshTrigger((prev) => prev + 1);
            setIsAddedLoading(false);
          }
        } else {
          setAddedRefreshTrigger((prev) => prev + 1);
          setIsAddedLoading(false);
        }

        if (activeTab === "eligible" && isSearchMode && searchValue.trim()) {
          setIsSearchLoading(true);
          DatasetService.searchEligibleDatasetRequests(
            Number(id),
            searchValue,
            responseKeywords.length > 0 ? responseKeywords : [],
            setIsSearchLoading,
            apiKey
          ).subscribe(
            (items: ChatCompletionRequest[]) => {
              setSearchResults(items);
              setEligibleRequests(
                items.slice(0, Math.min(eligiblePageSize, items.length))
              );
              setTotalEligible(items.length);
              setIsSearchLoading(false);
              setIsEligibleLoading(false);
            },
            (error: any) => {
              console.error(error);
              setIsSearchLoading(false);
              setIsEligibleLoading(false);
            }
          );
        } else {
          setEligibleRefreshTrigger((prev) => prev + 1);
          setIsEligibleLoading(false);
        }
      },
      (error: any) => {
        console.error(error);
        setIsAddedLoading(false);
        setIsEligibleLoading(false);
      }
    );
  };

  const clearSearch = () => {
    setSearchValue("");
    setResponseKeywords([]);
    setResponseKeywordInput("");
    setIsSearchMode(false);
    setSearchResults([]);
    setCurrentEligiblePage(1);
    eligibleRequestsCacheRef.current = {};

    // Always clear both session filters
    setSessionIdFilter("");
    setAppliedSessionIdFilter("");

    setEligibleRefreshTrigger((prev) => prev + 1);
  };

  const clearAddedSearch = () => {
    setAddedSearchValue("");
    setAddedResponseKeywords([]);
    setAddedResponseKeywordInput("");
    setIsAddedSearchMode(false);
    setAddedSearchResults([]);
    setCurrentAddedPage(1);
    addedRequestsCacheRef.current = {};
    setAddedRefreshTrigger((prev) => prev + 1);
  };

  // Augmentation handlers
  const handleAugmentClick = (request: ChatCompletionRequest) => {
    setSelectedAugmentRequest(request);
    setIsAugmentModalVisible(true);
  };

  const handleAugmentModalOk = () => {
    if (selectedAugmentRequest) {
      try {
        setIsAugmenting(true);
        DatasetService.augmentRequest(
          selectedAugmentRequest.request_id!,
          augmentRequestCount,
          setIsAugmenting,
          apiKey,
          Number(id)
        ).subscribe(
          () => {
            setIsAugmentModalVisible(false);
            setSelectedAugmentRequest(null);
            setAugmentRequestCount(1);
            setIsAugmenting(false);
            navigate(`/build/datasets/${id}?tab=augmentations`);
          },
          (error) => {
            console.error("Error augmenting request:", error);
            setIsAugmenting(false);
          }
        );
      } catch (error) {
        console.error("Error augmenting request:", error);
        setIsAugmenting(false);
      }
    }
  };

  const handleAugmentModalCancel = () => {
    setIsAugmentModalVisible(false);
    setSelectedAugmentRequest(null);
    setAugmentRequestCount(1);
  };

  // AI Review handlers
  const handleStartAiReview = (payload: AIReviewPayload) => {
    if (!id) return;

    setIsAiReviewLoading(true);
    DatasetService.startAiReview(
      Number(id),
      setIsAiReviewLoading,
      apiKey,
      payload
    ).subscribe(
      () => {
        setIsAiReviewModalVisible(false);

        if (dataset) {
          setDataset({
            ...dataset,
            meta: {
              ...dataset.meta,
              review_status: "CREATED",
            },
          });
        }
      },
      (error: any) => {
        console.error("Error starting AI review:", error);
      }
    );
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      const payload = {
        name: name,
        description: description,
      };

      DatasetService.updateDataset(
        Number(id),
        payload,
        setIsSaving,
        apiKey
      ).subscribe(
        () => {
          navigate(`/build/datasets/${id}`);
        },
        (error: any) => {
          console.error("Error updating dataset:", error);
          setIsSaving(false);
        }
      );
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  // Search controls for eligible requests
  const renderEligibleSearchControls = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Select value={searchType} onValueChange={setSearchType}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="query">Summary Query</SelectItem>
            <SelectItem value="session">Session</SelectItem>
            <SelectItem value="response">Response Keywords</SelectItem>
          </SelectContent>
        </Select>

        {searchType === "response" ? (
          <div className="flex-1 max-w-md">
            <div className="border border-border rounded-md p-2 min-h-[40px] flex flex-wrap gap-1 items-center bg-background">
              {responseKeywords.map((keyword, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
                >
                  {keyword}
                  <button
                    onClick={() => {
                      const newKeywords = [...responseKeywords];
                      newKeywords.splice(index, 1);
                      setResponseKeywords(newKeywords);
                    }}
                    className="ml-1 text-xs hover:text-red-600"
                  >
                    ×
                  </button>
                </span>
              ))}
              <Input
                placeholder={responseKeywords.length === 0 ? "Add response keywords..." : ""}
                value={responseKeywordInput}
                onChange={(e) => setResponseKeywordInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && responseKeywordInput.trim()) {
                    const value = responseKeywordInput.trim();
                    if (!responseKeywords.includes(value)) {
                      setResponseKeywords([...responseKeywords, value]);
                    }
                    setResponseKeywordInput("");
                  }
                  if (e.key === "Backspace" && responseKeywordInput === "" && responseKeywords.length > 0) {
                    const newKeywords = [...responseKeywords];
                    newKeywords.pop();
                    setResponseKeywords(newKeywords);
                  }
                }}
                onBlur={() => {
                  if (responseKeywordInput.trim()) {
                    const value = responseKeywordInput.trim();
                    if (!responseKeywords.includes(value)) {
                      setResponseKeywords([...responseKeywords, value]);
                    }
                    setResponseKeywordInput("");
                  }
                }}
                className="border-none shadow-none flex-1 min-w-[100px] p-0"
              />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-1 max-w-md">
            <Input
              placeholder={searchType === "query" ? "Search by query..." : "Search by session ID..."}
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                if (e.target.value === "") {
                  clearSearch();
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
              disabled={isSearchLoading}
            />
            <Button
              onClick={handleSearch}
              disabled={!searchValue.trim() || isSearchLoading}
              className="gap-2"
            >
              {isSearchLoading ? (
                <Loader2Icon className="w-4 h-4 animate-spin" />
              ) : (
                <SearchIcon className="w-4 h-4" />
              )}
            </Button>
          </div>
        )}

        {searchType === "response" && (
          <Button
            onClick={handleResponseKeywordSearch}
            disabled={responseKeywords.length === 0 || isSearchLoading}
            className="gap-2"
          >
            {isSearchLoading ? (
              <Loader2Icon className="w-4 h-4 animate-spin" />
            ) : (
              <SearchIcon className="w-4 h-4" />
            )}
          </Button>
        )}

        {(isSearchMode || appliedSessionIdFilter) && (
          <Button
            variant="outline"
            onClick={clearSearch}
            disabled={isSearchLoading}
            className="gap-2"
          >
            <XIcon className="w-4 h-4" />
            Clear {isSearchMode ? "Search" : "Filter"}
          </Button>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch
              checked={showOnlySelectedEligible}
              onCheckedChange={setShowOnlySelectedEligible}
            />
            <span className="text-sm">Show Only Selected</span>
          </div>
        </div>
        {selectedEligibleRequests.length > 0 && (
          <Button
            onClick={handleAddSelectedRequests}
            disabled={isEligibleLoading}
            className="gap-2"
          >
            {isEligibleLoading && <Loader2Icon className="w-4 h-4 animate-spin" />}
            Add Selected ({selectedEligibleRequests.length})
          </Button>
        )}
      </div>
    </div>
  );

  // Search controls for added requests
  const renderAddedSearchControls = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Select value={addedSearchType} onValueChange={setAddedSearchType}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="query">Summary Query</SelectItem>
            <SelectItem value="session">Session</SelectItem>
            <SelectItem value="response">Response Keywords</SelectItem>
          </SelectContent>
        </Select>

        {addedSearchType === "response" ? (
          <div className="flex-1 max-w-md">
            <div className="border border-border rounded-md p-2 min-h-[40px] flex flex-wrap gap-1 items-center bg-background">
              {addedResponseKeywords.map((keyword, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
                >
                  {keyword}
                  <button
                    onClick={() => {
                      const newKeywords = [...addedResponseKeywords];
                      newKeywords.splice(index, 1);
                      setAddedResponseKeywords(newKeywords);
                    }}
                    className="ml-1 text-xs hover:text-red-600"
                  >
                    ×
                  </button>
                </span>
              ))}
              <Input
                placeholder={addedResponseKeywords.length === 0 ? "Add response keywords..." : ""}
                value={addedResponseKeywordInput}
                onChange={(e) => setAddedResponseKeywordInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && addedResponseKeywordInput.trim()) {
                    const value = addedResponseKeywordInput.trim();
                    if (!addedResponseKeywords.includes(value)) {
                      setAddedResponseKeywords([...addedResponseKeywords, value]);
                    }
                    setAddedResponseKeywordInput("");
                  }
                  if (e.key === "Backspace" && addedResponseKeywordInput === "" && addedResponseKeywords.length > 0) {
                    const newKeywords = [...addedResponseKeywords];
                    newKeywords.pop();
                    setAddedResponseKeywords(newKeywords);
                  }
                }}
                onBlur={() => {
                  if (addedResponseKeywordInput.trim()) {
                    const value = addedResponseKeywordInput.trim();
                    if (!addedResponseKeywords.includes(value)) {
                      setAddedResponseKeywords([...addedResponseKeywords, value]);
                    }
                    setAddedResponseKeywordInput("");
                  }
                }}
                className="border-none shadow-none flex-1 min-w-[100px] p-0"
              />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-1 max-w-md">
            <Input
              placeholder={addedSearchType === "query" ? "Search by query..." : "Search by session ID..."}
              value={addedSearchValue}
              onChange={(e) => {
                setAddedSearchValue(e.target.value);
                if (e.target.value === "") {
                  clearAddedSearch();
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleAddedSearch();
                }
              }}
              disabled={isAddedSearchLoading}
            />
            <Button
              onClick={handleAddedSearch}
              disabled={!addedSearchValue.trim() || isAddedSearchLoading}
              className="gap-2"
            >
              {isAddedSearchLoading ? (
                <Loader2Icon className="w-4 h-4 animate-spin" />
              ) : (
                <SearchIcon className="w-4 h-4" />
              )}
            </Button>
          </div>
        )}

        {addedSearchType === "response" && (
          <Button
            onClick={handleAddedResponseKeywordSearch}
            disabled={addedResponseKeywords.length === 0 || isAddedSearchLoading}
            className="gap-2"
          >
            {isAddedSearchLoading ? (
              <Loader2Icon className="w-4 h-4 animate-spin" />
            ) : (
              <SearchIcon className="w-4 h-4" />
            )}
          </Button>
        )}

        {isAddedSearchMode && (
          <Button
            variant="outline"
            onClick={clearAddedSearch}
            disabled={isAddedSearchLoading}
            className="gap-2"
          >
            <XIcon className="w-4 h-4" />
            Clear Search
          </Button>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch
              checked={showOnlySelectedAdded}
              onCheckedChange={setShowOnlySelectedAdded}
            />
            <span className="text-sm">Show Only Selected</span>
          </div>
        </div>
        {selectedAddedRequests.length > 0 && (
          <Button
            variant="destructive"
            onClick={handleRemoveSelectedRequests}
            disabled={isAddedLoading}
            className="gap-2"
          >
            {isAddedLoading && <Loader2Icon className="w-4 h-4 animate-spin" />}
            Remove Selected ({selectedAddedRequests.length})
          </Button>
        )}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
          {/* Header Card Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="h-4 bg-muted rounded w-32 animate-pulse mb-6" />
              <div className="space-y-2">
                <div className="h-10 bg-muted rounded w-64 animate-pulse" />
                <div className="h-5 bg-muted rounded w-96 animate-pulse" />
              </div>
            </CardContent>
          </Card>

          {/* Content Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="h-4 bg-muted rounded flex-1 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-8 animate-pulse" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/build/compositions?tab=datasets")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Datasets
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button 
                    onClick={() => navigate(`/build/datasets/${id}`)}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    {dataset?.name}
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">Edit</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-1">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                Edit Dataset
              </h1>
              <p className="text-lg text-muted-foreground font-gc-reg">
                Modify dataset details and manage requests
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Dataset Details Card */}
        {dataset && (
          <Card className="border-border">
            <CardContent className="p-8">
              <h3 className="text-lg font-gc-med text-foreground mb-4">Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <ServerIcon className="w-4 h-4 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground font-gc-reg">Source</p>
                  </div>
                  <p className="text-base font-gc-reg text-foreground">
                    {dataset.intent_group_name &&
                    !dataset.application_id &&
                    !dataset.intent_id ? (
                      <button
                        onClick={() => navigate(`/intent-groups/${dataset.intent_group_id}`)}
                        className="text-maitai-green hover:underline"
                      >
                        {dataset.intent_group_name}
                      </button>
                    ) : dataset.application_name &&
                      dataset.intent_name &&
                      !dataset.intent_group_id ? (
                      <>
                        <button
                          onClick={() => navigate(`/application/${dataset.application_id}`)}
                          className="text-maitai-green hover:underline"
                        >
                          {dataset.application_name}
                        </button>
                        {" / "}
                        <button
                          onClick={() => navigate(`/application/${dataset.application_id}/intent/${dataset.intent_id}`)}
                          className="text-maitai-green hover:underline"
                        >
                          {dataset.intent_name}
                        </button>
                      </>
                    ) : (
                      "Unknown source"
                    )}
                  </p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground font-gc-reg">Date Created</p>
                  </div>
                  <p className="text-base font-gc-reg text-foreground">
                    {moment(dataset.date_created).format("MMM D, YYYY HH:mm:ss")}
                  </p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <FileTextIcon className="w-4 h-4 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground font-gc-reg">Total Requests</p>
                  </div>
                  <p className="text-base font-gc-reg text-foreground">
                    {dataset.request_count || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Form Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-gc-med text-foreground">Name</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter dataset name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="text-sm font-gc-med text-foreground">Description</Label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                  placeholder="Enter dataset description"
                  rows={4}
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>

              {tagDistribution.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-gc-med text-foreground">Request Distribution</Label>
                  <div className="flex flex-wrap gap-2">
                    {(showAllTags
                      ? tagDistribution
                      : tagDistribution.slice(0, 20)
                    ).map(({ tag, count }) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
                      >
                        {tag} ({count})
                      </span>
                    ))}
                    {tagDistribution.length > 20 && !showAllTags && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowAllTags(true)}
                        className="text-xs"
                      >
                        Show All ({tagDistribution.length})
                      </Button>
                    )}
                    {showAllTags && tagDistribution.length > 20 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowAllTags(false)}
                        className="text-xs"
                      >
                        Show Less
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tabs Card */}
        <Card className="border-border">
          <CardContent className="p-0">
            {/* Tab Navigation */}
            <div className="border-b border-border">
              <nav className="flex space-x-8 px-8 py-4">
                <button
                  onClick={() => setActiveTab("added")}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 text-sm font-gc-med rounded-md transition-colors",
                    activeTab === "added"
                      ? "bg-maitai-green/10 text-maitai-green"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                  )}
                >
                  <FileTextIcon className="w-4 h-4" />
                  Added Requests
                </button>
                <button
                  onClick={() => setActiveTab("eligible")}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 text-sm font-gc-med rounded-md transition-colors",
                    activeTab === "eligible"
                      ? "bg-maitai-green/10 text-maitai-green"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                  )}
                >
                  <PlusIcon className="w-4 h-4" />
                  Eligible Requests
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-8">
              {activeTab === "added" ? (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-gc-med text-foreground">Added Requests</h3>
                      <p className="text-sm text-muted-foreground">Manage requests in this dataset</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        onClick={() => setIsAiReviewModalVisible(true)}
                        className="gap-2"
                      >
                        <FlaskConicalIcon className="w-4 h-4" />
                        AI Review
                      </Button>
                    </div>
                  </div>

                  {renderAddedSearchControls()}

                  <DatasetAddedRequestsTable
                    requests={addedRequests}
                    totalRequests={totalAdded}
                    searchResults={addedSearchResults}
                    currentPage={currentAddedPage}
                    pageSize={addedPageSize}
                    loading={isAddedLoading}
                    isSearchMode={isAddedSearchMode}
                    isSearchLoading={isAddedSearchLoading}
                    searchType={addedSearchType}
                    searchValue={addedSearchValue}
                    responseKeywords={addedResponseKeywords}
                    responseKeywordInput={addedResponseKeywordInput}
                    selectedRequests={selectedAddedRequests}
                    showOnlySelected={showOnlySelectedAdded}
                    requestTypeFilters={requestTypeFilters}
                    onPageChange={setCurrentAddedPage}
                    onSearch={handleAddedSearch}
                    onClearSearch={clearAddedSearch}
                    onSearchTypeChange={setAddedSearchType}
                    onSearchValueChange={setAddedSearchValue}
                    onResponseKeywordsChange={setAddedResponseKeywords}
                    onResponseKeywordInputChange={setAddedResponseKeywordInput}
                    onSelectedRequestsChange={setSelectedAddedRequests}
                    onShowOnlySelectedChange={setShowOnlySelectedAdded}
                    onRequestTypeFiltersChange={setRequestTypeFilters}
                    onRemoveSelectedRequests={handleRemoveSelectedRequests}
                    onViewRequest={handleViewRequest}
                    onAugmentRequest={handleAugmentClick}
                    onRequestSave={(request) => handleRequestSave(request, "added")}
                    datasetId={id}
                    apiKey={apiKey}
                  />
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-gc-med text-foreground">Eligible Requests</h3>
                      <p className="text-sm text-muted-foreground">Select requests to add to this dataset</p>
                    </div>
                  </div>

                  {renderEligibleSearchControls()}

                  <DatasetEligibleRequestsTable
                    requests={eligibleRequests}
                    totalRequests={totalEligible}
                    searchResults={searchResults}
                    currentPage={currentEligiblePage}
                    pageSize={eligiblePageSize}
                    loading={isEligibleLoading}
                    isSearchMode={isSearchMode}
                    isSearchLoading={isSearchLoading}
                    searchType={searchType}
                    searchValue={searchValue}
                    responseKeywords={responseKeywords}
                    responseKeywordInput={responseKeywordInput}
                    sessionIdFilter={sessionIdFilter}
                    appliedSessionIdFilter={appliedSessionIdFilter}
                    selectedRequests={selectedEligibleRequests}
                    showOnlySelected={showOnlySelectedEligible}
                    requestTypeFilters={requestTypeFilters}
                    onPageChange={setCurrentEligiblePage}
                    onSearch={handleSearch}
                    onClearSearch={clearSearch}
                    onSearchTypeChange={setSearchType}
                    onSearchValueChange={setSearchValue}
                    onResponseKeywordsChange={setResponseKeywords}
                    onResponseKeywordInputChange={setResponseKeywordInput}
                    onSessionIdFilterChange={setSessionIdFilter}
                    onSelectedRequestsChange={setSelectedEligibleRequests}
                    onShowOnlySelectedChange={setShowOnlySelectedEligible}
                    onRequestTypeFiltersChange={setRequestTypeFilters}
                    onAddSelectedRequests={handleAddSelectedRequests}
                    onViewRequest={handleViewRequest}
                    onAugmentRequest={handleAugmentClick}
                    onRequestSave={(request) => handleRequestSave(request, "eligible")}
                    datasetId={id}
                    apiKey={apiKey}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => navigate(`/build/datasets/${id}`)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="gap-2"
          >
            {isSaving && <Loader2Icon className="w-4 h-4 animate-spin" />}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Request View Modal */}
      {selectedRequest && (
        <RequestViewModal
          request={selectedRequest}
          isVisible={isModalVisible}
          onClose={handleCloseModal}
          apiKey={apiKey}
        />
      )}

      {/* AI Review Modal */}
      <AIReview
        isVisible={isAiReviewModalVisible}
        onClose={() => setIsAiReviewModalVisible(false)}
        onStartReview={handleStartAiReview}
        loading={isAiReviewLoading}
      />

      {/* Augmentation Modal */}
      {isAugmentModalVisible && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardContent className="p-6">
              <div className="space-y-4">
                <h3 className="text-lg font-gc-med text-foreground">Augment Request</h3>
                <p className="text-sm text-muted-foreground">
                  Augmenting this request will create semantically similar variations while preserving the original intent to enhance dataset diversity.
                </p>
                <div className="space-y-2">
                  <Label htmlFor="count" className="text-sm font-gc-med text-foreground">Number of requests to create</Label>
                  <Input
                    id="count"
                    type="number"
                    min={1}
                    max={100}
                    value={augmentRequestCount}
                    onChange={(e) => setAugmentRequestCount(Number(e.target.value) || 1)}
                  />
                  <p className="text-xs text-muted-foreground">Enter a number between 1 and 100</p>
                </div>
                <div className="flex items-center justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={handleAugmentModalCancel}
                    disabled={isAugmenting}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAugmentModalOk}
                    disabled={isAugmenting}
                    className="gap-2"
                  >
                    {isAugmenting && <Loader2Icon className="w-4 h-4 animate-spin" />}
                    Augment
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DatasetEdit;
